import React, { useMemo, useRef, useState, useEffect } from 'react'
import ChevronRight from '../../../../icons/ChevronRight';
import RadioSelect from '../../../../components/RadioSelect/RadioSelect'
import OptionCard from './OptionCard';
import { DHL_PACKSTATION, PERSONAL_ADDRESS } from '../constants';
import { useShippingPageContext } from '../providers/ShippingPageProvider';
import { translate } from '../../../../utils/translator';
import CMSModal from '../../../StartConsultation/CMSModal';
import Sprite from "../../../../images/StartConsultation/sprite-main.png";
import { useCartContext } from '../../../../customHooks/useCart';
import { clientConfig } from '../../../../config';
import PharmacyIcon from "../../../../icons/pharmacy-icon.svg";
import PharmacyRow from './PharmacyRow';
import ShippingOptionsSelector, { ShippingOption } from './ShippingOptionsSelector';
import { resolveExpressShippingTiers } from '../utils/expressShippingTiers';
import { SERVICE_OPTIONS } from '../../../../utils/consts';
import { SELF_PICKUP_PAYMENT_MODELS } from '../constants';
import { resolveCarePlusShipping } from '../../../CarePlusShared/carePlusCartTotals';
import { shouldShowSelfPickupSubscriptionDisclaimer } from '../utils/rxSelfPickupSubscription';

const GERMANY_COUNTRY_ID = "DE";

type Props = {
    code: string,
    title: string,
    isSelected: boolean
    onSelect: () => void
    children?: React.ReactNode
    hideOptionCards?: boolean
    isDisabled?: boolean
    hideHeader?: boolean //For example in self pickup flow
    isOtcOnlyFlow?: boolean 
    onPartnerPharmaciesOpen?: () => void
    onPharmacyDetailsExpand?: () => void
}


const DeliveryOptionPanel: React.FC<Props> = ({ code, title, isSelected, onSelect, children, hideOptionCards = false ,isDisabled = false, hideHeader = false, isOtcOnlyFlow = false, onPartnerPharmaciesOpen, onPharmacyDetailsExpand }) => {

    const { language, country, handleDeliveryOptionChange, expressDeliveryState, preSelectedExternalPharmacy, selfPickupPharmacySettings, contentType, isMixedOtcTestLabCart, allowPrescriptionRedemption, allPharmaciesLocations, availableExpressTiers, isExpressFlow, isUrbifyFullyRolledOut, rxSelfPickup, selectedPickupPharmacyCode, rxSelfPickupServiceOption, setIsPickupSheetOpen, isCannabisCatalog, isRxPickupFastApproval, pharmacyRequiredError, setPharmacyRequiredError, isCarePlusOrder } = useShippingPageContext();
    
    const { getCart } = useCartContext();
    const cart = getCart();
    //TODO: remove the false incase we want this flag
    // /const isCarePlusOrder = false && Number(cart.care_plus_plan_fee ?? 0) > 0 || searchParams.get('care_plus') === '1' || searchParams.get('cat') === 'weight_loss_2';
    const isPreOrderCart = cart.products?.some((p: any) => p.is_pre_orderable) ?? false;
    const isMixedCart = isPreOrderCart && (cart.products?.some((p: any) => !p.is_pre_orderable) ?? false);

    // HL-11357: the server says a delivered one-off purchase is not orderable for this product
    // (settings.disallow_one_time_premium, no subscription plan on the cart). OrdersController
    // rejects such an order as one_time_premium_not_allowed, so NOTHING that offers delivery may
    // be put in front of the customer — not the DHL/express option cards below, and not the
    // DHL/express shipping selector, which is a second, independent way to pick DHL and would
    // otherwise silently clear the prescription-only choice via update-delivery-option.
    // Strict false: a cart from before this field existed keeps the old behaviour, and GB has no
    // prescription-only card to fall back to.
    const onlyPrescriptionIsTheOnlyOption =
        cart.allow_one_time_premium === false && country?.toUpperCase() !== 'GB';
    // Care+: Choose Plan already decided prescription-only vs delivered. Prescription only is shown
    // alone (and stays selected); any other plan never offers it.
    const isCarePlusPrescriptionOnly = Boolean(isCarePlusOrder) &&
        (Boolean(cart.only_prescription) || cart.selected_service_option === SERVICE_OPTIONS.ONLY_PRESCRIPTION);
    const { pharmaciesModalInfo } = useShippingPageContext();

    const [optionCardsTitle, setOptionCardsTitle] = useState<string>("");
    const optionPanelHeaderRef = useRef<HTMLDivElement | null>(null);
    // One-shot: fixtures/slow API can leave selected_service_option null after update;
    // without this, unstable deps re-fire the effect forever (Jest hang).
    const autoSelectAttempted = useRef(false);
    const getPanelId = (suffix: string) => `${code}-${suffix}`;

    const handleOnSelect = async () => {

        // If the option is disabled, or already selected, don't do anything
        if (isDisabled || isSelected) {
            return;
        }
        onSelect();

        // If the express option is selected, we need to change the delivery option to regular
        if (expressDeliveryState.isSelected) {
            await handleDeliveryOptionChange("regular");
        }



        //Wait for the body to open then scroll to the header
        setTimeout(() => {
            optionPanelHeaderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50)
    };



    // Self-pickup option card
    const formatSelfPickupOptionCard = () => {
        
        const description =  (
            <div className="bg-white flex flex-col gap-2 border border-[#EDEDED] rounded-lg">
                <span className="text-[12px] text-gray-400 font-roboto">{translate(language, "shipping", "serviceOptions/pickup_from_pharmacy_description")}</span>
                
                { Array.from(new Set(cart.products.map((product) => product.stock_code || "").filter(Boolean))).map((stockCode) => {
                    
                    const stock = allPharmaciesLocations?.[stockCode]; 
                    if (!stock) return null;
                    return (
                        <div
                            key={stockCode}
                            className="p-3 border border-solid border-[#EDEDED] rounded-lg overflow-hidden"
                        >
                            <div className="font-medium text-sm text-[#0D2C54] truncate">
                                {stock.name}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                                {stock.address}
                            </div>
                        </div>
                    );
                })}

            </div>
        )
            
        
        return {
            description: description,
            title: translate(language, "shipping", "default/pickup_title"),
            boldDescription: '',
            badgeText: '',
            headerImageSrc: '',
            headerIconSrc: PharmacyIcon,
            isSelected: true,
            onSelect,
            isDisabled: false
        }
    }


    // RX Self-Pickup option card
    const formatRxSelfPickupOptionCard = () => {
        const isSelected = cart.selected_service_option === SERVICE_OPTIONS.SELF_PICKUP;
        const isSubscription = Boolean(cart.subscription_plan_id);
        // Mirror the same subscription-aware resolution used in SelfPickupPharmacySheet.
        const resolvedServiceOption = isSubscription ? SELF_PICKUP_PAYMENT_MODELS.PAY_ONCE : rxSelfPickupServiceOption;
        const rawActiveCards = resolvedServiceOption === SELF_PICKUP_PAYMENT_MODELS.PRESCRIPTION_ONLY
            ? rxSelfPickup.prescriptionOnlyPharmacyCards
            : rxSelfPickup.pharmacyCards;
        const activeCards = isSubscription
            ? Object.fromEntries(Object.entries(rawActiveCards).filter(([, card]: [string, any]) => card.for_subscription))
            : rawActiveCards;
        const selectedPharmacy = selectedPickupPharmacyCode ? activeCards[selectedPickupPharmacyCode] : null;

        const paymentModelBase = resolvedServiceOption === SELF_PICKUP_PAYMENT_MODELS.PRESCRIPTION_ONLY
            ? translate(language, "shipping", "RxSelfPickup/model_prescription_only_title")
            : translate(language, "shipping", "RxSelfPickup/model_pay_once_title");
        const paymentModelLabel = isRxPickupFastApproval
            ? `${paymentModelBase} ${translate(language, "shipping", "RxSelfPickup/fast_doctor_review_suffix")}`
            : paymentModelBase;

        // Only the first subscription order is picked up — renewals ship by DHL (same as the Uber disclaimer).
        const subscriptionDisclaimer = shouldShowSelfPickupSubscriptionDisclaimer(cart) && (
            <span className="font-roboto font-semibold">{translate(language, "shipping", "RxSelfPickup/self_pickup_subscription_disclaimer")}</span>
        );

        const description = isSelected && selectedPharmacy ? (
            // State 3: pharmacy chosen — description text + "Pharmacy" label + card with chevron
            <div className="flex flex-col gap-2">
                <span className="text-[12px] text-[#037d73] font-poppins leading-[15px]">
                    {translate(language, "shipping", "RxSelfPickup/option_row_description")}
                </span>
                {subscriptionDisclaimer}
                <span className="text-[13px] font-semibold text-[#98a0af] font-poppins">
                    {translate(language, "shipping", "RxSelfPickup/pharmacy_label")}
                </span>
                <div
                    className="bg-white rounded-[6px] flex items-center justify-between pl-3 pr-5 py-2.5 cursor-pointer overflow-hidden"
                    style={{ border: "1px solid #dbe1eb" }}
                    onClick={(e) => { e.stopPropagation(); setIsPickupSheetOpen(true); }}
                >
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                        <span className="font-medium text-[14px] text-[#3e3e3e] truncate">{selectedPharmacy.name}</span>
                        <span className="text-[12px] text-[#666] truncate">{selectedPharmacy.address}</span>
                        <span className="text-[12px] text-[#99a3b2]">{paymentModelLabel}</span>
                    </div>
                    <ChevronRight />
                </div>
            </div>
        ) : (
            // State: selected but no pharmacy yet — description text + CTA button
            <div className="flex flex-col gap-4">
                <span className="text-[12px] text-[#037d73] font-poppins leading-[15px]">
                    {translate(language, "shipping", "RxSelfPickup/option_row_description")}
                </span>
                {subscriptionDisclaimer}
                <button
                    onClick={(e) => { e.stopPropagation(); setPharmacyRequiredError(false); setIsPickupSheetOpen(true); }}
                    className="h-14 rounded-xl font-poppins font-bold text-base text-white border-none cursor-pointer mr-auto"
                    style={{ background: "linear-gradient(90deg, #269c6e 0%, #7aba47 100%)", minWidth: 250, padding: "0 75px" }}
                >
                    {translate(language, "shipping", "RxSelfPickup/choose_pharmacy")}
                </button>
                {pharmacyRequiredError && isSelected && !selectedPickupPharmacyCode && (
                    <span className="text-[12px] text-[#e53e3e] font-poppins">
                        {translate(language, "shipping", "RxSelfPickup/select_pharmacy_required")}
                    </span>
                )}
            </div>
        );

        return {
            title: translate(language, "shipping", "RxSelfPickup/option_row_title"),
            description,
            boldDescription: '',
            badgeText: '',
            headerImageSrc: '',
            headerIconSrc: '',
            isSelected,
            onSelect: () => { if (!isSelected) { handleDeliveryOptionChange("self_pickup"); setIsPickupSheetOpen(true); } },
            isDisabled: false,
            isDeliveryOption: false,
            noSelectedBackground: true,
            showHeaderSeparator: true,
            noDescriptionIndent: true,
            hasError: pharmacyRequiredError && isSelected && !selectedPickupPharmacyCode,
        };
    };

    // Cannaleo option card
    const formatCannaeloOptionCard = () => {


        const pharmacyName = preSelectedExternalPharmacy?.name || null;
        const pharmacyUnofficialName = preSelectedExternalPharmacy?.cannabis_pharmacy_name || null;
        
        const title = pharmacyName
        ? `Ihre gewählte Apotheke: ${pharmacyName} ${pharmacyUnofficialName ?  "(" + pharmacyUnofficialName + ")" : ""}`
        : "Ihre gewählte Apotheke";

        const description = pharmacyName
            ? `Sobald Ihre Bestellung bezahlt ist und Ihr Arzt das Rezept genehmigt hat, wird es direkt an die ${pharmacyName} geschickt. Sie können sich dann mit der Apotheke in Verbindung setzen und das Medikament bezahlen.`
            : "Sobald Ihre Bestellung bezahlt ist und Ihr Arzt das Rezept genehmigt hat, wird das Rezept an die von Ihnen gewählte Apotheke geschickt. Detaillierte Anweisungen und die nächsten Schritte finden Sie in Ihrer E-Mail.";

        return {
            title,
            description,
            boldDescription: '',
            badgeText: '',
            headerImageSrc: '',
            headerIconSrc: PharmacyIcon,
            isSelected: true,
            onSelect: async () => {
                await handleDeliveryOptionChange("only_prescription");
            },
            isDisabled: false,
            isDeliveryOption: false
        }
    }
    

    const buildPharmacyModalDescription = (isCH: boolean): React.ReactNode => {
        const descKey = isCH ? "serviceOptions/service_option_1_description_ch" : "serviceOptions/service_option_1_description";
        const base = translate(language, "shipping", descKey) as string;
        const keyword = 'Partnerapotheken';
        const idx = base.indexOf(keyword);

        if (idx === -1 || !pharmaciesModalInfo) return base;

        return (
            <>
                {base.slice(0, idx)}
                <CMSModal
                    className="max-w-[530px] max-h-[550px]"
                    centerTitle={true}
                    image={<img src={Sprite} className="mb-4 w-[63px] h-[65px] object-none object-[-815px_-137px]" alt="Sprite" />}
                    overlayClassName="bg-black/50"
                    overlayZIndex={9999}
                    disableBodyScrollLock={true}
                    trigger={
                        <a href="#" className="relative text-[#269C6E] no-underline group">
                            {keyword}
                            <span className="absolute left-0 bottom-0 h-[1px] w-0 bg-[#269C6E] transition-all duration-500 ease-in-out group-hover:w-full" />
                        </a>
                    }
                    title={pharmaciesModalInfo?.title || ''}
                    subtitle={pharmaciesModalInfo?.subtitle || ''}
                    content={
                        <div className="h-[244px] overflow-y-auto pr-4" onClick={(e) => e.stopPropagation()}>
                            {pharmaciesModalInfo?.pharmacies?.map((pharmacy, index) => (
                                <PharmacyRow key={`${pharmacy.name || 'pharmacy'}-${index}`} pharmacy={pharmacy} index={index} onExpand={onPharmacyDetailsExpand} />
                            ))}
                        </div>
                    }
                    onOpen={onPartnerPharmaciesOpen}
                />
                {base.slice(idx + keyword.length)}
            </>
        );
    }

    // Medication option card
    const formatMedicationOptionCard = () => {
        const isOtc = isOtcOnlyFlow && contentType !== "test_lab";
        const isCH = country?.toLowerCase() === "ch";

        const description = isOtc
            ? translate(language, "shipping", `serviceOptions/otc_delivery_description_${country?.toLowerCase()}`)
            : buildPharmacyModalDescription(isCH);

        return {
            title: translate(language, "shipping", isCH ? "serviceOptions/service_option_1_title_ch" : "serviceOptions/service_option_1_title"),
            description,
            badgeText: translate(language, "shipping", "serviceOptions/service_option_1_badge_text"),
            badgeColor: "#59CE3B",
            headerImageSrc: '',
            headerIconSrc: '',
            isSelected: country?.toUpperCase() === GERMANY_COUNTRY_ID
                ? cart.selected_service_option === SERVICE_OPTIONS.REGULAR
                : !cart.only_prescription && !expressDeliveryState.isSelected,
            onSelect: async () => await handleDeliveryOptionChange("regular"),
            isDisabled: false,
            isDeliveryOption: true
        }
    }

    const expressOptionDescription = () => {
        const baseDescription = translate(language, "shipping", "serviceOptions/service_option_3_description").replace("[Name]", expressDeliveryState.expressPharmacyName) as string;
        const descriptionWithName = expressDeliveryState.expressPharmacyName
            ? baseDescription.replace("{hier die ausgewählte Apotheke einfügen}", expressDeliveryState.expressPharmacyName)
            : baseDescription;
        return (
            <span className='flex flex-col gap-2'>
                {descriptionWithName}
                {
                    cart.subscription_plan_id &&
                    <>
                        <br />
                        <span className='font-roboto font-semibold'>{translate(language, "shipping", "serviceOptions/express_subscription_disclaimer")}</span>
                    </>
                }
            </span>
        )
    }

    // Express option card
    const formatExpressOptionCard = () => {

        return {
            title: translate(language, "shipping", "serviceOptions/service_option_3_title"),
            description: expressOptionDescription(),
            boldDescription: '',
            badgeText: translate(language, "shipping", "serviceOptions/service_option_3_badge_text"),
            badgeColor: "#0A9281",
            headerImageSrc: '',
            headerIconSrc: '',
            isSelected: country?.toUpperCase() === GERMANY_COUNTRY_ID ? 
                (cart.selected_service_option === SERVICE_OPTIONS.EXPRESS) :
                expressDeliveryState.isSelected,
            onSelect: async () => {
                if (!expressDeliveryState.isSelected && cart.isExpressFlow) {
                    await handleDeliveryOptionChange("express");
                }
            },
            isDisabled: !expressDeliveryState.isEnabled,
            // onSelect above is a no-op without cart.isExpressFlow (next-day eligibility alone makes
            // express "available"), so the load-time auto-select must skip this card or nothing
            // gets selected. Rendering and manual clicks are unchanged.
            skipAutoSelect: !cart.isExpressFlow,
            isDeliveryOption: true
        }

    }

    // Test lab / Mixed OTC+Test lab option card (same shape, different text)
    const formatTestLabOptionCard = (mixed = false) => {
        const title = mixed
            ? "DHL Lieferung"
            : translate(language, "shipping", `serviceOptions/test_lab_delivery_title_${country?.toLowerCase()}`);
        const description = mixed
            ? "Ihr Testkit und Ihre OTC-Produkte werden separat versendet.\nDas Testkit wird per DHL geliefert, während Ihre OTC-Produkte in einem separaten Paket versendet werden. Beide Sendungen werden an die unten ausgewählte Adresse geliefert. Lieferzeiten und Sendungsverfolgung können je nach Paket unterschiedlich sein."
            : translate(language, "shipping", `serviceOptions/test_lab_delivery_description_${country?.toLowerCase()}`);

        return {
            title,
            description,
            boldDescription: '',
            badgeText: '',
            headerImageSrc: '',
            headerIconSrc: '',
            isSelected: true,
            onSelect: async () => await handleDeliveryOptionChange("regular"),
            isDisabled: false,
            isDeliveryOption: true
        }
    }


    // Prescription only option card
    const formatOnlyPrescriptionOptionCard = () => {

        let title = allowPrescriptionRedemption ? translate(language, "shipping", "serviceOptions/service_option_2_cannabis_title") : translate(language, "shipping", `serviceOptions/service_option_2_title_${country?.toLowerCase()}`)
        let description =  (
            <div className="flex flex-col gap-2">
                <span className="text-[12px] text-gray-400 font-roboto">{allowPrescriptionRedemption ? translate(language, "shipping", "serviceOptions/service_option_2_cannabis_description") : translate(language, "shipping", `serviceOptions/service_option_2_description_${country?.toLowerCase()}`)}</span>
                
                { Array.from(new Set(cart.products.map((product) => product.owner_id || "").filter(Boolean))).map((ownerID) => {
                    
                    const pharmacy = allPharmaciesLocations?.[ownerID];
                    if (!pharmacy) return null;
                    return (
                        <div
                            key={ownerID}
                            className="p-3 border border-solid border-[#EDEDED] rounded-lg"
                        >
                            <div className="font-medium text-sm text-[#0D2C54]">
                                {pharmacy.name}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                                {pharmacy.address}
                            </div>
                        </div>
                    );
                })}

            </div>
        )

        if (cart.flow === "schengen") {
            title = translate(language, "shipping", "serviceOptions/service_option_2_schengen_title");
            description = translate(language, "shipping", "serviceOptions/service_option_2_schengen_description");
        }

        return {
            title,
            description,
            boldDescription: '',
            badgeText: '',
            headerImageSrc: '',
            isSelected: country?.toUpperCase() === GERMANY_COUNTRY_ID ? 
                (cart.selected_service_option === SERVICE_OPTIONS.ONLY_PRESCRIPTION) :
                cart.only_prescription!,
            headerIconSrc: '',
            onSelect: async () => {
                await handleDeliveryOptionChange("only_prescription");
            },
            isDisabled: Boolean(cart.subscription_plan_id) || country?.toUpperCase() === "GB",
            isDeliveryOption: false
        }
    }


    // DHL Packstation option card
    const formatDHLPackstationOptionCard = () => {
        return  {
            title: translate(language, "shipping", "serviceOptions/dhl_packstation_title"),
            isSelected: true,
            description: '',
            boldDescription: '',
            badgeText: '',
            headerImageSrc: clientConfig.IMAGES_URL + "uploads/pages/144/dhl-delivery.png",
            headerIconSrc:'',
            onSelect: () => {},
            isDisabled: false,
            isDeliveryOption: false
        }
    }

    const renderOptionCard = (card: any, index: number) => {
        return (
            <OptionCard
                key={card.title + index}
                idPrefix={getPanelId(`option-card-${index}`)}
                title={card.title}
                onSelect={card.onSelect}
                isSelected={card.isSelected}
                description={card.description}
                boldDescription={card.boldDescription}
                headerImageSrc={card.headerImageSrc}
                headerIconSrc={card.headerIconSrc}
                badgeText={card.badgeText}
                badgeColor={card.badgeColor}
                isDisabled={card.isDisabled}
                alwaysShowBody={card.alwaysShowBody}
                noSelectedBackground={card.noSelectedBackground}
                showHeaderSeparator={card.showHeaderSeparator}
                noDescriptionIndent={card.noDescriptionIndent}
                hasError={card.hasError}
                orange={isCarePlusOrder}
            />
        );
    }

    // Option cards
    const optionCards = useMemo(() => {
        const optionCards:any[] = []

        if (code === PERSONAL_ADDRESS) {

            if (hideOptionCards) {
                return optionCards
            }

            // Cannelo flow
            if (preSelectedExternalPharmacy) {
                optionCards.push(formatCannaeloOptionCard())
            }

            // Germany flow
            else if (contentType === "cannabis") {
                switch (cart.selected_service_option) {
                    case "express":
                        optionCards.push(formatExpressOptionCard())
                        break;
                    case "only_prescription":
                        optionCards.push(formatOnlyPrescriptionOptionCard())
                        break;
                    case "self_pickup":
                        optionCards.push(formatSelfPickupOptionCard())
                        break
                    default:
                        optionCards.push(formatMedicationOptionCard())
                        break;
                }
            }

            // Self pick up flow (cannabis / legacy only — not for RX self-pickup)
            else if (selfPickupPharmacySettings && !rxSelfPickup?.isEligible) {
                setOptionCardsTitle("Ihre gewählte Apotheke");
                optionCards.push(formatSelfPickupOptionCard());
            }

            // Prescription only flow
            else if (cart.allow_only_prescription && !cart.product_in_stock && country?.toUpperCase() !== 'GB') {
                optionCards.push(formatOnlyPrescriptionOptionCard())
            }

            // STI (test_kits) Flow
            else if (contentType === "test_kit") {
                if (expressDeliveryState.isAvailable) {
                    optionCards.push(formatExpressOptionCard())
                }
                optionCards.push(formatMedicationOptionCard())
            }

            // Mixed OTC + Test lab flow
            else if (isMixedOtcTestLabCart) {
                optionCards.push(formatTestLabOptionCard(true))
            }

            // Test lab flow – only DHL, no Express
            else if (contentType === "test_lab") {
                optionCards.push(formatTestLabOptionCard())
            }

            // Regular flow (Medication + prescription only + express)
            else {
                setOptionCardsTitle(translate(language, "shipping", "serviceOptions/service_options_title"));

                if (isCarePlusPrescriptionOnly) {
                    optionCards.push(formatOnlyPrescriptionOptionCard())
                    return optionCards
                }
                if (!onlyPrescriptionIsTheOnlyOption) {
                    if (expressDeliveryState.isAvailable) {
                        optionCards.push(formatExpressOptionCard())
                    }
                    optionCards.push(formatMedicationOptionCard())
                }
                if (rxSelfPickup.isEligible && !isCannabisCatalog && !isCarePlusOrder && country?.toUpperCase() === GERMANY_COUNTRY_ID) {
                    optionCards.push(formatRxSelfPickupOptionCard())
                }
                // Care+ keeps it only when no delivered option is orderable, so the panel is never empty.
                if (country?.toUpperCase() !== 'GB' && (!isCarePlusOrder || onlyPrescriptionIsTheOnlyOption)) {
                    optionCards.push(formatOnlyPrescriptionOptionCard())
                }
            }
        }

        if (code === DHL_PACKSTATION) {
            optionCards.push(formatDHLPackstationOptionCard())
        }

        return optionCards

    }, [code, cart, preSelectedExternalPharmacy, selfPickupPharmacySettings, hideOptionCards, expressDeliveryState.isEnabled, expressDeliveryState.isSelected, expressDeliveryState.deliveryPrice, contentType, isMixedOtcTestLabCart, rxSelfPickup, selectedPickupPharmacyCode, rxSelfPickupServiceOption, isCannabisCatalog, onlyPrescriptionIsTheOnlyOption, pharmacyRequiredError, isCarePlusOrder, isCarePlusPrescriptionOnly])

    const isNextDayDisplayed = useMemo(() => {
    if (code !== PERSONAL_ADDRESS) return false;
    const { tiersToShow } = resolveExpressShippingTiers(availableExpressTiers, {
        ignoreAbTestGate: contentType !== "cannabis" && isUrbifyFullyRolledOut,
    });
    return tiersToShow.some(t => t.tier === 'next_day') && country?.toUpperCase() === GERMANY_COUNTRY_ID;
}, [availableExpressTiers, code, country, contentType, isUrbifyFullyRolledOut]);

// Persist the DHL delivery time that was displayed to the customer so the
// thankyou page can show the matching estimate in step 3.
useEffect(() => {
    if (code === PERSONAL_ADDRESS && country?.toUpperCase() === GERMANY_COUNTRY_ID) {
        localStorage.setItem('dhl_next_day_displayed', isNextDayDisplayed ? '2' : '1');
    }
}, [isNextDayDisplayed, code, country]);

const shippingOptions = useMemo((): ShippingOption[] | null => {

        const selfPickupServiceOptions = [SERVICE_OPTIONS.SELF_PICKUP, SERVICE_OPTIONS.ONLY_PRESCRIPTION];
        if (code !== PERSONAL_ADDRESS) return null;

        // allow_only_prescription only permits the Rx-only flow; delivery is off only when the product is out of stock.
        if (contentType !== "cannabis" && cart.allow_only_prescription && !cart.product_in_stock) return null;
        // Every option this selector offers (DHL and every express tier) is a delivered purchase,
        // so when delivery is not orderable at all there is nothing legal left to show.
        if (onlyPrescriptionIsTheOnlyOption) return null;
        // Care+ prescription only: nothing ships, and picking DHL/express here would undo the choice.
        if (isCarePlusPrescriptionOnly) return null;

        // Cannabis: still show DHL when express tiers are cleared (e.g. address change out of range).
        if (contentType === "cannabis") {
            if (availableExpressTiers.length === 0 || selfPickupServiceOptions.includes(cart.selected_service_option!)) {
                return null;
            }
        }


        const options: ShippingOption[] = [];

        const { tiersToShow, showDhl } = resolveExpressShippingTiers(availableExpressTiers, {
            ignoreAbTestGate: contentType !== "cannabis" && isUrbifyFullyRolledOut,
        });

        if (isExpressFlow && !isPreOrderCart) {
            for (const tier of tiersToShow) {
                const tierDescription = translate(language, "shipping", `serviceOptions/service_option_${tier.tier}_description`) as string;
                const description: React.ReactNode = cart.subscription_plan_id ? (
                    <span className="flex flex-col gap-2">
                        {tierDescription}
                        <br />
                        <span className="font-roboto font-semibold">{translate(language, "shipping", "serviceOptions/express_subscription_disclaimer")}</span>
                    </span>
                ) : tierDescription;
                options.push({
                    id: tier.tier,
                    title: translate(language, "shipping", `serviceOptions/service_option_${tier.tier}_title`) as string,
                    price: tier.fee,
                    description,
                    isDisabled: tier.isCutoffPassed === true,
                    disabledReason: tier.isCutoffPassed
                        ? (translate(language, "shipping", "serviceOptions/service_option_cutoff_passed") as string)
                        : undefined,
                });
            }
        }

        if (showDhl) {
            // HL-11357: a Care+ plan bonus that covers shipping in full leaves regular_shipping_fee
            // at the raw fee — the discount reaches the cart as a lump bonus_value — so without
            // this the row bills the customer for shipping the plan already paid for. The server
            // reports coverage per fee (cart.care_plus_waived_fees), computed by the same engine
            // that marks the fee free on the Choose Plan card. The shared resolver also keeps this
            // tile consistent with the Shipping right-panel breakdown.
            const shipping = resolveCarePlusShipping(cart.regular_shipping_fee, cart.care_plus_waived_fees);
            const isShippingWaived = shipping.originalFee !== undefined;
            const dhlTitleKey = isPreOrderCart
                ? `serviceOptions/pre_order_dhl_title_${country?.toLowerCase()}`
                : contentType === "test_lab"
                ? `serviceOptions/test_lab_dhl_title_${country?.toLowerCase()}`  // kits ship in 2-4 business days, not 24-48h
                : (isNextDayDisplayed
                    ? `serviceOptions/service_option_dhl_title_${country?.toLowerCase()}_next_day`
                    : `serviceOptions/service_option_dhl_title_${country?.toLowerCase()}`);
            options.push({
                id: 'dhl',
                title: translate(language, "shipping", dhlTitleKey) as string,
                price: shipping.fee,
                // The struck pair says "free" more precisely than the label does, so it wins.
                originalPrice: shipping.originalFee,
                priceLabel: !isShippingWaived && shipping.isFree
                    ? (translate(language, "shipping", "serviceOptions/service_option_free_shipping") as string)
                    : undefined,
            });
        }

        const selectableCount = options.filter(o => !o.isDisabled).length;
        return selectableCount >= 1 ? options : null;
}, [availableExpressTiers, cart.shipping_company_code, cart.regular_shipping_fee, cart.care_plus_waived_fees, cart.selected_service_option, cart.subscription_plan_id, cart.allow_only_prescription, cart.product_in_stock, language, code, country, contentType, isUrbifyFullyRolledOut, isPreOrderCart, isNextDayDisplayed, onlyPrescriptionIsTheOnlyOption, isCarePlusPrescriptionOnly]);

    // Selection truth lives on the cart (forwarded from choose-treatment, or written by a
    // click via update-delivery-option). Read it directly — no local state, no auto-select.
    const selectedShippingOptionId = useMemo(() => {
        if (cart.selected_service_option === SERVICE_OPTIONS.EXPRESS) {
            return cart.selected_express_tier ?? 'dhl';
        }
        if (cart.selected_service_option === SERVICE_OPTIONS.ONLY_PRESCRIPTION || cart.selected_service_option === SERVICE_OPTIONS.SELF_PICKUP) {
            return null;
        }
        return 'dhl';
    }, [cart.selected_service_option, cart.selected_express_tier]);

    const handleShippingOptionSelect = async (optionId: string) => {
        if (optionId === 'dhl') {
            await handleDeliveryOptionChange("regular", undefined, { isManualSelection: true });
        } else {
            await handleDeliveryOptionChange("express", optionId, { isManualSelection: true });
        }
    };

    // Auto-select the default option when no service option is set on the cart
    useEffect(() => {
        if (!cart.selected_service_option && !autoSelectAttempted.current) {
            // Flowzz/Canaleo and other PO carts often arrive with only_prescription=true but
            // selected_service_option=null. Prefer PO so we never auto-pick regular/express
            // (which clears only_prescription via update-delivery-option).
            const autoSelect = async () => {
                if (cart.only_prescription) {
                    autoSelectAttempted.current = true;
                    await handleDeliveryOptionChange("only_prescription");
                    return;
                }
                // Auto-select the only available option
                if (optionCards.length === 1) {
                    const singleOption = optionCards[0];
                    if (singleOption.onSelect) {
                        autoSelectAttempted.current = true;
                        await singleOption.onSelect();
                    }
                } else {
                    // If there are multiple options, select the first one that is not disabled
                    const firstAvailableOption = optionCards.find(o => !o.isDisabled && !o.skipAutoSelect);
                    if (firstAvailableOption && firstAvailableOption.onSelect) {
                        autoSelectAttempted.current = true;
                        await firstAvailableOption.onSelect();
                    }
                }
            };
            void autoSelect();
        }
    }, [country, cart.selected_service_option, optionCards.length, optionCards]);

    return (
        <div
            id={isSelected ? 'selected-delivery-option' : 'option-panel'}
            style={isSelected ? { border: '1px solid var(--border-focus)' } : {}}
            className='rounded-[6px] bg-white divide-y-2 '>

            {/* Header  */}
            {
                (!hideHeader || country?.toUpperCase() === GERMANY_COUNTRY_ID) && (
                    <div
                        ref={optionPanelHeaderRef}
                        id={isSelected ? 'selected-delivery-option-header' : 'option-panel-header'}
                        onClick={handleOnSelect}
                        className={`flex items-center p-[20px] scroll-mt-[50px] ${isDisabled ? "cursor-not-allowed bg-[#EEF1F6]" : isSelected ? "cursor-default" : "cursor-pointer"} `} >
                        {/* Radio */}
                        { country?.toUpperCase() !== GERMANY_COUNTRY_ID &&
                            <RadioSelect selected={isSelected} disabled={false} />
                        }

                        {/* Title  */}
                        <span id={getPanelId('option-panel-title')} className={`text-[14px] font-medium ${isDisabled ? "text-[#98a0af]" : "text-[#0d2c54]"} `}>{title}</span>
                    </div>
                )
            }


            {/* Body  */}
            {
                isSelected ?

                    <div id='option-panel-body' className='px-[20px] md:px-[25px] py-[20px]' style={{ borderTop: "1px solid #dbe1eb" }} >

                        { shippingOptions ? (
                            <>
                                <ShippingOptionsSelector
                                    options={shippingOptions}
                                    selectedId={selectedShippingOptionId}
                                    onSelect={handleShippingOptionSelect}
                                    label={translate(language, "shipping", "serviceOptions/shipping_options_label") as string}
                                    footerDescription={!isPreOrderCart ? translate(language, "shipping", "serviceOptions/service_option_1_description") as string : undefined}
                                    orange={isCarePlusOrder}
                                />
                                {isPreOrderCart && (
                                    <p className="font-roboto font-medium text-[13px] text-[#00734a] leading-[19.5px] mt-2">
                                        {translate(language, "shipping", `serviceOptions/pre_order_dhl_description_${country?.toLowerCase()}`)}
                                    </p>
                                )}
                                {isMixedCart && (
                                    <div className="bg-white border border-solid border-[#b5bdcc] rounded-[6px] overflow-hidden p-[20px] mt-2 font-roboto text-[13px] text-[#656d79] leading-[19.5px]">
                                        {translate(language, "shipping", `serviceOptions/pre_order_mixed_cart_disclaimer_${country?.toLowerCase()}`)}
                                    </div>
                                )}
                                {!isOtcOnlyFlow && !isPreOrderCart &&
                                    optionCards
                                        .filter((card: any) => !card.isDeliveryOption)
                                        .map((card: any, index: number) => renderOptionCard(card, index))
                                }
                            </>
                        ) : contentType !== "cannabis" ? (
                            <>
                            {/* Title  */}
                            {(optionCardsTitle) ?
                                <span id={getPanelId('option-panel-body-title')} style={{ fontFamily: "Roboto, sans-serif" }} className='font-semibold text-[#98a0af] text-[13px] mb-[30px] block'>
                                    {optionCardsTitle}
                                </span>
                                : null
                            }
        
                            {/* optionCards  */}
                            {
                        (
                            // Test lab / Mixed OTC+Test lab flow – single card, no radio button
                            contentType === "test_lab" || isMixedOtcTestLabCart ?
                                <div className="flex flex-col p-4 mt-2 w-full rounded-md" style={{ backgroundColor: "#F7FAEF", border: "1px solid #EEF1F6" }}>
                                    {optionCards[0]?.title && (
                                        <p className="font-poppins text-sm font-medium text-[#0d2c54]">{optionCards[0].title}</p>
                                    )}
                                    {optionCards[0]?.description && (
                                        <div className="font-poppins font-normal text-[13px] leading-[19.5px] text-[#656d79] mt-2 flex flex-col gap-[8px]">{typeof optionCards[0].description === 'string' ? optionCards[0].description.split('\n').map((line: string, i: number) => <p key={i}>{line}</p>) : optionCards[0].description}</div>
                                    )}
                                </div>
                            :

                            // Express Delivery is unavailable
                            !expressDeliveryState.isAvailable ?
                                optionCards.map((card, index) => {
                                    return (
                                        renderOptionCard(card, index)
                                    )
                                })
                            :

                            // Express Delivery is available
                            <>
                                { optionCards.length > 1 && <div id='delivery-options-container' className={`rounded-[10px] mb-[20px]  w-full ${
                                        optionCards.filter(card => card.isDeliveryOption).length > 1 && 'border border-solid border-[#269C6E] md:pl-4 md:pr-4'
                                }`}
                                >
                                    {
                                        optionCards.map((card, index) => 
                                            card.isDeliveryOption ? renderOptionCard(card, index) : null
                                        )
                                    }
                                </div>}
                                { !isOtcOnlyFlow &&
                                <div id='prescription-option-container'>
                                    {
                                        optionCards.map((card, index) => 
                                            !card.isDeliveryOption ? renderOptionCard(card, index) : null
                                        )
                                    }
                                </div>
                                }
                            </>
                        )
                        }
                            </>
                        ) : (
                            // Cannabis flow — no tiers available, single card fallback
                            <>
                                {optionCards[0]?.title && (
                                    <p id={getPanelId("inline-option-card-title")} className={`font-poppins text-xs text-black font-semibold `}>{optionCards[0]?.title}</p>
                                )}

                                <div id={getPanelId("inline-option-card-body")} className="flex flex-col mt-2 gap-y-[5px] z-[3]">
                                    {optionCards[0]?.description && (
                                    <div id={getPanelId("inline-option-card-description")} className="font-poppins font-normal text-[13px] leading-[19.5px] text-[#656d79] flex flex-col gap-[8px]">
                                        {typeof optionCards[0]?.description === 'string'
                                            ? optionCards[0].description.split('\n').map((line: string, i: number) => <p key={i}>{line}</p>)
                                            : optionCards[0]?.description}
                                    </div>
                                )}
                                {optionCards[0]?.boldDescription && (
                                    <div id={getPanelId("inline-option-card-bold-description")} className="font-roboto font-semibold text-[13px] text-[#656d79]">{optionCards[0]?.boldDescription}</div>
                                )}

                                </div>
                            </>
                        )}

                        {/* Children (Most likely form) */}
                        {children}

                    </div>
                    :
                    null
            }

        </div>
    )

}

export default DeliveryOptionPanel



